import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import AshdaleLogoSVG from '../../assets/ashdale-logo.svg';

const { FiMail, FiArrowLeft, FiCheckCircle, FiAlertCircle } = FiIcons;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { resetPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sm:mx-auto sm:w-full sm:max-w-md"
      >
        <div className="text-center">
          <img src={AshdaleLogoSVG} alt="Ashdale Analytics" className="h-12 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-white">Reset Password</h2>
          <p className="text-secondary-400 mt-2">
            Enter your email and we'll send you a recovery link
          </p>
        </div>

        <div className="mt-8 bg-secondary-900 py-8 px-4 shadow-xl sm:rounded-xl sm:px-10 border border-primary-500/10">
          {success ? (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-900/20 mb-4">
                <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-green-500" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Check your inbox</h3>
              <p className="text-secondary-400 mb-6">
                We've sent password reset instructions to <strong>{email}</strong>
              </p>

              <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 text-left mb-8">
                <h4 className="flex items-center font-bold text-yellow-500 mb-2">
                  <SafeIcon icon={FiAlertCircle} className="w-5 h-5 mr-2" />
                  Localhost Error?
                </h4>
                <p className="text-sm text-yellow-400/90 mb-3">
                  If you click the email link and get a <strong>"localhost refused to connect"</strong> error, you need to update your Supabase settings:
                </p>
                <ol className="list-decimal list-inside text-sm text-yellow-500/80 space-y-1">
                  <li>Go to your Supabase Dashboard</li>
                  <li>Click <strong>Authentication</strong> &gt; <strong>URL Configuration</strong></li>
                  <li>Change the <strong>Site URL</strong> to match your current browser URL (remove anything after .io or .com)</li>
                  <li>Save and click the email link again</li>
                </ol>
              </div>

              <Link 
                to="/login" 
                className="inline-flex items-center text-primary-500 hover:text-primary-400 font-bold"
              >
                <SafeIcon icon={FiArrowLeft} className="mr-2" />
                Back to Sign In
              </Link>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-900/20 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm flex items-center">
                  <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-2 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-secondary-300 mb-1">Email</label>
                <div className="relative">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full px-4 py-3 pl-11 bg-black border border-secondary-800 rounded-lg text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    placeholder="name@company.com"
                  />
                  <SafeIcon icon={FiMail} className="absolute left-4 top-3.5 h-5 w-5 text-secondary-500" />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-primary-500 hover:bg-primary-400 text-black font-bold rounded-lg transition-all active:scale-[0.98] disabled:opacity-50 shadow-lg shadow-primary-500/20"
              >
                {loading ? 'Sending link...' : 'Send Reset Link'}
              </button>

              <div className="text-center">
                <Link 
                  to="/login" 
                  className="inline-flex items-center text-sm text-secondary-400 hover:text-white transition-colors"
                >
                  <SafeIcon icon={FiArrowLeft} className="mr-2" />
                  Return to Sign In
                </Link>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;